import { createApi } from '@reduxjs/toolkit/query/react'
import {
  GetCompanyByIdPayload,
  Company,
  CompanyAssociation,
  UpdateCompanyAssociationPayload,
  CompanyEmail,
  AddCompanyEmailRequest,
  CompanyPhone,
  AddCompanyPhoneRequest,
  UpdateCompanyEmailRequest,
  UpdateCompanyPhoneRequest,
  CreateCompanyPayload,
  CompanyCreateResponse,
  CreateUserCompanyAssociationPayload,
  CarrierAssociation,
  CarrierCompany,
  CreateUserCarrierAssociationPayload,
  UpdateCarrierAssociationPayload,
} from './company.types'
import { createBaseQueryWithReauth } from 'store/auth/base-query-with-reauth'

const baseUrl = `${process.env.REACT_APP_ONBOARDING_SERVICE_API}`

export const companyApi = createApi({
  reducerPath: 'companyApi',
  baseQuery: createBaseQueryWithReauth(baseUrl!),
  tagTypes: ['Company', 'UserCompanyAssociations', 'UserCarrierAssociations'],
  endpoints: (builder) => ({
    getCompanyById: builder.query<Company, GetCompanyByIdPayload>({
      query: ({ id }) => ({
        url: `oapi/v1/company/getCompanyById/${id}`,
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'Company', id }],
    }),

    createCompany: builder.mutation<CompanyCreateResponse, CreateCompanyPayload>({
      query: (body) => ({
        url: 'oapi/v1/company/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Company', 'UserCompanyAssociations'],
    }),

    createUserCompanyAssociation: builder.mutation<
      CompanyAssociation,
      CreateUserCompanyAssociationPayload
    >({
      query: (body) => ({
        url: 'oapi/v1/user-company-associations',
        method: 'POST',
        body: {
          userId: body.userId,
          companyId: body.companyId,
          association: body.association,
          factoringEnabled: body.factoringEnabled ?? false,
          factoringTerm: body.factoringTerm ?? 0,
          currency: body.currency ?? 'CAD',
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'UserCompanyAssociations', id: arg.userId },
      ],
    }),

    updateCompanyById: builder.mutation<Company, Partial<Company>>({
      query: (body) => ({
        url: `oapi/v1/company/update/${body.id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Company', id }],
    }),

    getAllCompanies: builder.query<Company[], void>({
      query: () => ({
        url: 'oapi/v1/company/getAllCompanies',
      }),
    }),

    getUserCompanyAssociationByUser: builder.query<
      CompanyAssociation[],
      { userId: string }
    >({
      query: ({ userId }) => ({
        url: `oapi/v1/user-company-associations/users/${userId}`,
      }),
      providesTags: (_result, _error, { userId }) => [
        { type: 'UserCompanyAssociations', id: userId },
      ],
    }),

    getUserCompanyAssociationByCompany: builder.query<
      CompanyAssociation[],
      { companyId: string }
    >({
      query: ({ companyId }) => ({
        url: `oapi/v1/user-company-associations/companies/${companyId}`,
      }),
    }),

    updateUserCompanyAssociation: builder.mutation<
      CompanyAssociation,
      UpdateCompanyAssociationPayload
    >({
      query: ({ id, ...body }) => ({
        url: `oapi/v1/user-company-associations/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: 'UserCompanyAssociations', id: userId },
      ],
    }),

    deleteUserCompanyAssociation: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `oapi/v1/user-company-associations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserCompanyAssociations'],
    }),

    getCompanyEmails: builder.query<CompanyEmail[], { companyId: string }>({
      query: ({ companyId }) => ({
        url: `oapi/v1/company/${companyId}/emails`,
      }),
      providesTags: (_result, _error, { companyId }) => [
        { type: 'Company', id: companyId },
        'Company',
      ],
    }),

    addCompanyEmail: builder.mutation<
      CompanyEmail,
      { companyId: string; body: AddCompanyEmailRequest }
    >({
      query: ({ companyId, body }) => ({
        url: `oapi/v1/company/${companyId}/emails`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { companyId }) => [
        { type: 'Company', id: companyId },
        'Company',
      ],
    }),

    deleteCompanyEmail: builder.mutation<void, { companyEmailId: string }>({
      query: ({ companyEmailId }) => ({
        url: `oapi/v1/company/emails/${companyEmailId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Company'],
    }),

    updateCompanyEmail: builder.mutation<
      CompanyEmail,
      { companyEmailId: string; body: UpdateCompanyEmailRequest }
    >({
      query: ({ companyEmailId, body }) => ({
        url: `oapi/v1/company/emails/${companyEmailId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Company'],
    }),

    getCompanyPhones: builder.query<CompanyPhone[], { companyId: string }>({
      query: ({ companyId }) => ({
        url: `oapi/v1/company/${companyId}/phones`,
      }),
      providesTags: (_result, _error, { companyId }) => [
        { type: 'Company', id: companyId },
        'Company',
      ],
    }),

    addCompanyPhone: builder.mutation<
      CompanyPhone,
      { companyId: string; body: AddCompanyPhoneRequest }
    >({
      query: ({ companyId, body }) => ({
        url: `oapi/v1/company/${companyId}/phones`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { companyId }) => [
        { type: 'Company', id: companyId },
        'Company',
      ],
    }),

    deleteCompanyPhone: builder.mutation<void, { companyPhoneId: string }>({
      query: ({ companyPhoneId }) => ({
        url: `oapi/v1/company/phones/${companyPhoneId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Company'],
    }),

    updateCompanyPhone: builder.mutation<
      CompanyPhone,
      { companyPhoneId: string; body: UpdateCompanyPhoneRequest }
    >({
      query: ({ companyPhoneId, body }) => ({
        url: `oapi/v1/company/phones/${companyPhoneId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Company'],
    }),

    getUserCarrierAssociationByUser: builder.query<
      CarrierAssociation[],
      { userId: string }
    >({
      query: ({ userId }) => ({
        url: `oapi/v1/user-carrier-associations/users/${userId}`,
      }),
      providesTags: (_result, _error, { userId }) => [
        { type: 'UserCarrierAssociations', id: userId },
      ],
    }),

    getCarrierCompaniesByUser: builder.query<CarrierCompany[], { userId: string }>({
      query: ({ userId }) => ({
        url: `oapi/v1/user-carrier-associations/users/${userId}/companies`,
      }),
      providesTags: (_result, _error, { userId }) => [
        { type: 'UserCarrierAssociations', id: userId },
      ],
    }),

    createUserCarrierAssociation: builder.mutation<
      CarrierAssociation,
      CreateUserCarrierAssociationPayload
    >({
      query: (body) => ({
        url: 'oapi/v1/user-carrier-associations',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'UserCarrierAssociations', id: arg.userId },
      ],
    }),

    updateUserCarrierAssociation: builder.mutation<
      CarrierAssociation,
      UpdateCarrierAssociationPayload
    >({
      query: ({ id, ...body }) => ({
        url: `oapi/v1/user-carrier-associations/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['UserCarrierAssociations'],
    }),

    deleteUserCarrierAssociation: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `oapi/v1/user-carrier-associations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserCarrierAssociations'],
    }),
  }),
})

export const {
  useGetCompanyByIdQuery,
  useLazyGetCompanyByIdQuery,
  useCreateCompanyMutation,
  useCreateUserCompanyAssociationMutation,
  useUpdateCompanyByIdMutation,
  useGetAllCompaniesQuery,
  useGetUserCompanyAssociationByUserQuery,
  useGetUserCompanyAssociationByCompanyQuery,
  useUpdateUserCompanyAssociationMutation,
  useDeleteUserCompanyAssociationMutation,
  useGetCompanyEmailsQuery,
  useAddCompanyEmailMutation,
  useDeleteCompanyEmailMutation,
  useUpdateCompanyEmailMutation,
  useGetCompanyPhonesQuery,
  useAddCompanyPhoneMutation,
  useDeleteCompanyPhoneMutation,
  useUpdateCompanyPhoneMutation,
  useGetUserCarrierAssociationByUserQuery,
  useGetCarrierCompaniesByUserQuery,
  useCreateUserCarrierAssociationMutation,
  useUpdateUserCarrierAssociationMutation,
  useDeleteUserCarrierAssociationMutation,
} = companyApi
